import { Button } from "@arco-design/web-react";
import { GoogleOutlined } from "@ant-design/icons";
import { fetchSignInMethodsForEmail, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { useDispatch } from "react-redux";
import { setUser } from "../store/userSlice";
import { endpoints } from "../api/endpoints";
import axios from "axios";
import { routes } from "../routes/routes";
import { completeAuthRedirect } from "../utils/authReturnUrl";
import { Message } from "@arco-design/web-react";
import { auth } from "../firebaseConfig";
import { isAuthPopupClosedByUser } from "../utils/isAuthPopupClosedByUser";
import { splitFullName } from "../utils/nameUtils";
import { FirebaseError } from "firebase/app";
import { useTranslation } from "react-i18next";
import { getOAuthButtonProps } from "../utils/oauthButtonStyle";
import { trackSignUp } from "../analytics/track";

type GoogleRegisterButtonProps = {
    isMentor: boolean;
    isGuest?: boolean;
    variant?: "default" | "icon";
    signupSource?: string;
    redirectPath?: string;
};

export const GoogleRegisterButton = ({ isMentor, isGuest = false, variant = "default", signupSource, redirectPath }: GoogleRegisterButtonProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const handleGoogleRegister = async () => {

        const provider = new GoogleAuthProvider();

        try {
            const result = await signInWithPopup(auth, provider); 

            if(result) {
                const firebaseUser = result.user; 
                const idToken = await firebaseUser.getIdToken();

                // Split the full name into first and last name
                const { name, last_name } = splitFullName(firebaseUser.displayName);

                await axios.post(
                    endpoints.signup,
                    {
                        name,
                        last_name,
                        email: firebaseUser.email,
                        is_mentor: isGuest ? false : isMentor,
                        is_guest: isGuest,
                        is_confirmed: true,
                        ...(signupSource ? { source: signupSource } : {}),
                    },
                    {
                        headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" },
                    }
                );

                const whoami = await axios.get(endpoints.whoami, {
                    headers: { Authorization: `Bearer ${idToken}` },
                });
                dispatch(setUser({ ...whoami.data }));

                trackSignUp({
                    method: "google",
                    role: isGuest ? "guest" : isMentor ? "mentor" : "mentee",
                    source: signupSource,
                });

                Message.success({ content: t('notification_messages.sign_up_success'), closable: true, duration: 3000 });
                completeAuthRedirect(redirectPath ?? routes.profile);
            }
        } catch (error) {
            console.error("Google registration failed:", error);

            if (error instanceof FirebaseError && error.code === "auth/account-exists-with-different-credential") {
                Message.error(t('notification_messages.email_exists_with_outlook_error'));
                return;
            }

            if (isAuthPopupClosedByUser(error)) {
                return;
            } 

            Message.error(t('notification_messages.sign_up_error'));
        }
    };

    if (variant === "icon") {
        return (
            <Button
                className="auth-sso-btn"
                onClick={handleGoogleRegister}
                icon={<GoogleOutlined />}
                aria-label={t('register_page.google_sign_up')}
            />
        );
    }

    return (
        <Button
            onClick={handleGoogleRegister}
            shape="round"
            size="large"
            icon={<GoogleOutlined />}
            {...getOAuthButtonProps()}
        >
            {t('register_page.google_sign_up')}
        </Button>
    );
};
